import { useEffect, useRef, useState } from "react";

import { MAX_CELL_WIDTH, MIN_CELL_WIDTH } from "@corca/tiptap";

import { findNearestColumnEdge, getCellPos } from "./utils";

import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import type { RefObject } from "react";

type DragState = {
  colIndex: number;
  startX: number;
  startWidth: number;
  initialEdgeX: number;
  currentDelta: number;
};

type ColumnResizeState = {
  guideX: number;
};

export function useColumnResize(
  containerRef: RefObject<HTMLDivElement | null>,
  editor: Editor,
  node: Node,
  getPos: () => number | undefined,
): ColumnResizeState | null {
  const dragRef = useRef<DragState | null>(null);
  const [resizeState, setResizeState] = useState<ColumnResizeState | null>(null);
  const nodeRef = useRef(node);
  nodeRef.current = node;

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    function stopDrag() {
      document.removeEventListener("mousemove", handleDocMouseMove);
      document.removeEventListener("mouseup", handleDocMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    function handleDocMouseMove(e: MouseEvent) {
      const drag = dragRef.current;

      if (!drag) {
        return;
      }

      e.preventDefault();

      const delta = e.clientX - drag.startX;
      const newWidth = drag.startWidth + delta;
      const clampedWidth = Math.min(MAX_CELL_WIDTH, Math.max(MIN_CELL_WIDTH, newWidth));
      const clampedDelta = clampedWidth - drag.startWidth;

      drag.currentDelta = clampedDelta;

      setResizeState({ guideX: drag.initialEdgeX + clampedDelta });
    }

    function handleDocMouseUp() {
      const drag = dragRef.current;

      stopDrag();
      setResizeState(null);

      dragRef.current = null;

      if (!drag || Math.abs(drag.currentDelta) < 1) {
        return;
      }

      const tablePos = getPos();

      if (tablePos === undefined) {
        return;
      }

      const newWidth = Math.round(
        Math.min(MAX_CELL_WIDTH, Math.max(MIN_CELL_WIDTH, drag.startWidth + drag.currentDelta)),
      );
      const cellPos = getCellPos(nodeRef.current, tablePos, drag.colIndex);

      editor.commands.setColumnWidth(newWidth, cellPos);
    }

    function handleMouseDown(e: MouseEvent) {
      if (!editor.isEditable || !container || e.button !== 0) {
        return;
      }

      const edge = findNearestColumnEdge(container, e.clientX);

      if (!edge) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      dragRef.current = {
        colIndex: edge.colIndex,
        startX: e.clientX,
        startWidth: edge.width,
        initialEdgeX: edge.rightEdgeX,
        currentDelta: 0,
      };

      setResizeState({ guideX: edge.rightEdgeX });

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      document.addEventListener("mousemove", handleDocMouseMove);
      document.addEventListener("mouseup", handleDocMouseUp);
    }

    function handleDoubleClick(e: MouseEvent) {
      if (!editor.isEditable || !container) {
        return;
      }

      const tablePos = getPos();

      if (tablePos === undefined) {
        return;
      }

      const edge = findNearestColumnEdge(container, e.clientX);

      if (!edge) {
        return;
      }

      e.stopImmediatePropagation();

      const cellPos = getCellPos(nodeRef.current, tablePos, edge.colIndex);
      editor.commands.setColumnWidth(null, cellPos);
    }

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("dblclick", handleDoubleClick);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("dblclick", handleDoubleClick);
      stopDrag();
    };
  }, [containerRef, editor, getPos]);

  return resizeState;
}
